import React from 'react';
import { X, Phone, Mail, Calendar, FileText, Clock, Package, MessageSquare, Tag, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export const DetailModal = ({ item, type, onClose }) => {
  if (!item) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColor = () => {
    switch (type) {
      case 'rdv': return 'blue';
      case 'devis': return 'green';
      case 'diagnostic': return 'purple';
      default: return 'gray';
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'rdv': return 'Rendez-vous';
      case 'devis': return 'Devis';
      case 'diagnostic': return 'Diagnostic';
      default: return 'Commande';
    }
  };

  const color = getTypeColor();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className={`bg-${color}-600 text-white p-6 rounded-t-2xl relative`} style={{
          background: type === 'rdv' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' :
                     type === 'devis' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                     'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
        }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
          >
            <X size={24} />
          </button>
          <div className="flex items-center space-x-3 mb-2">
            <FileText size={32} />
            <div>
              <h2 className="text-2xl font-bold">{item.name}</h2>
              <p className="text-sm opacity-90">{getTypeLabel()} #{item.id?.substring(0, 8)}</p>
            </div>
          </div>
          <Badge className="bg-white bg-opacity-20 text-white border-0 mt-2">
            {item.status || 'En attente'}
          </Badge>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Date et heure */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-gray-700 mb-2">
              <Calendar className={`text-${color}-600`} size={20} />
              <span className="font-semibold">Date de la demande</span>
            </div>
            <p className="text-gray-900 ml-7">
              {type === 'rdv' && item.date ? 
                `${new Date(item.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} à ${item.time}` :
                formatDate(item.timestamp)
              }
            </p>
          </div>

          {/* Coordonnées */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-gray-700 mb-2">
                <Phone className={`text-${color}-600`} size={20} />
                <span className="font-semibold">Téléphone</span>
              </div>
              <a 
                href={`tel:${item.phone}`}
                className={`text-${color}-600 hover:underline font-medium ml-7 block`}
              >
                {item.phone}
              </a>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-gray-700 mb-2">
                <Mail className={`text-${color}-600`} size={20} />
                <span className="font-semibold">Email</span>
              </div>
              <a 
                href={`mailto:${item.email}`}
                className={`text-${color}-600 hover:underline font-medium ml-7 block text-sm`}
              >
                {item.email}
              </a>
            </div>
          </div>

          {/* Détails spécifiques selon le type */}
          {type === 'rdv' && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <Package className="text-blue-600" size={20} />
                      <span className="font-semibold">Appareil</span>
                    </div>
                    <p className="text-gray-900 ml-7">{item.device}</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <FileText className="text-blue-600" size={20} />
                      <span className="font-semibold">Service</span>
                    </div>
                    <p className="text-gray-900 ml-7">{item.service}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center space-x-2 text-gray-700 mb-2">
                    <Clock className="text-blue-600" size={20} />
                    <span className="font-semibold">Durée estimée</span>
                  </div>
                  <p className="text-gray-900 ml-7">{item.duration}h</p>
                </div>
              </div>
            </div>
          )}

          {type === 'devis' && (
            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <Package className="text-green-600" size={20} />
                      <span className="font-semibold">Type d'appareil</span>
                    </div>
                    <p className="text-gray-900 ml-7 capitalize">{item.deviceType}</p>
                  </div>
                  {item.brand && (
                    <div>
                      <div className="flex items-center space-x-2 text-gray-700 mb-2">
                        <Tag className="text-green-600" size={20} />
                        <span className="font-semibold">Marque</span>
                      </div>
                      <p className="text-gray-900 ml-7">{item.brand}</p>
                    </div>
                  )}
                </div>
                {item.model && (
                  <div className="mt-4">
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <FileText className="text-green-600" size={20} />
                      <span className="font-semibold">Modèle</span>
                    </div>
                    <p className="text-gray-900 ml-7">{item.model}</p>
                  </div>
                )}
                {item.promo_code && (
                  <div className="mt-4 bg-yellow-100 border-2 border-yellow-400 rounded-lg p-3">
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <Gift className="text-yellow-600" size={20} />
                      <span className="font-semibold">Code promo généré</span>
                    </div>
                    <p className="text-yellow-900 font-bold text-xl ml-7">{item.promo_code}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {type === 'diagnostic' && (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <Package className="text-purple-600" size={20} />
                      <span className="font-semibold">Type d'appareil</span>
                    </div>
                    <p className="text-gray-900 ml-7 capitalize">{item.deviceType}</p>
                  </div>
                  {item.brand && (
                    <div>
                      <div className="flex items-center space-x-2 text-gray-700 mb-2">
                        <Tag className="text-purple-600" size={20} />
                        <span className="font-semibold">Marque</span>
                      </div>
                      <p className="text-gray-900 ml-7">{item.brand}</p>
                    </div>
                  )}
                </div>
                {item.model && (
                  <div className="mt-4">
                    <div className="flex items-center space-x-2 text-gray-700 mb-2">
                      <FileText className="text-purple-600" size={20} />
                      <span className="font-semibold">Modèle</span>
                    </div>
                    <p className="text-gray-900 ml-7">{item.model}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Message/Problème */}
          {item.problem && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-gray-700 mb-3">
                <MessageSquare className={`text-${color}-600`} size={20} />
                <span className="font-semibold">Description du problème</span>
              </div>
              <p className="text-gray-900 ml-7 whitespace-pre-wrap leading-relaxed">{item.problem}</p>
            </div>
          )}

          {item.message && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-gray-700 mb-3">
                <MessageSquare className={`text-${color}-600`} size={20} />
                <span className="font-semibold">Message</span>
              </div>
              <p className="text-gray-900 ml-7 whitespace-pre-wrap leading-relaxed">{item.message}</p>
            </div>
          )}

          {/* Informations supplémentaires */}
          <div className="bg-gray-100 rounded-lg p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-3">Informations complémentaires</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">ID de la commande:</span>
                <span className="font-mono text-gray-900">{item.id?.substring(0, 12)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Statut:</span>
                <Badge className={`bg-${color}-500`}>{item.status || 'En attente'}</Badge>
              </div>
              {item.archived !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Archivé:</span>
                  <span className="font-medium">{item.archived ? '✅ Oui' : '❌ Non'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 rounded-b-2xl border-t">
          <Button
            onClick={onClose}
            className={`w-full bg-${color}-600 hover:bg-${color}-700 text-white font-semibold py-3`}
            style={{
              background: type === 'rdv' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' :
                         type === 'devis' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                         'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
            }}
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};
